/**
 * Shared paragraph-cloning helpers used by both `module-insert.ts` (CoC
 * single-section insert) and `qta-insert.ts` (Question To Answer multi-
 * paragraph insert).
 *
 * The big lesson from the CoC implementation: when we synthesise new
 * paragraphs into the docx editor doc, we MUST NOT copy the whole
 * `node.attrs` object from a donor paragraph — the docx-editor's
 * `ParagraphAttrs` carries identity (`paraId`, `textId`) and structural
 * (`_sectionProperties`, `sectionBreakType`, `pageBreakBefore`,
 * `pPrIns/Del`, list-numbering refs) fields that must remain unique per
 * paragraph. Cloning them silently corrupts the renderer's tracking and
 * the inserted block vanishes after a save/reload. `visualParaAttrs()`
 * allowlists only the *visual* attrs that are safe to share.
 */
import { type Mark, type Node as PMNode, type Schema } from "prosemirror-model";

const SAFE_PARA_ATTR_KEYS = new Set<string>([
  "styleId",
  "_originalFormatting",
  "alignment",
  "outlineLevel",
  "spaceBefore",
  "spaceAfter",
  "lineSpacing",
  "lineSpacingRule",
  "spacingExplicit",
  "indentLeft",
  "indentRight",
  "indentFirstLine",
  "hangingIndent",
  "keepNext",
  "keepLines",
  "contextualSpacing",
  "borders",
  "shading",
  "tabs",
  "bidi",
  "defaultTextFormatting",
]);

/** Visual-only subset of a paragraph node's attrs — safe to apply to a
 *  freshly synthesised paragraph without breaking renderer identity or
 *  duplicating structural markers. Returns `null` if the donor is null. */
export const visualParaAttrs = (
  node: PMNode | null
): Record<string, unknown> | null => {
  if (!node) return null;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(node.attrs)) {
    if (!SAFE_PARA_ATTR_KEYS.has(k)) continue;
    const v = (node.attrs as Record<string, unknown>)[k];
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
};

/** First-text-child marks from a paragraph node — used as a stand-in for
 *  "the style of this paragraph's run". Heading paragraphs in OOXML keep
 *  their visible font / size / bold on the inline runs, not on the
 *  paragraph node, so cloning these onto our new text gives the inserted
 *  heading the same visual weight as the donor. */
export const firstRunMarks = (node: PMNode | null): readonly Mark[] => {
  if (!node) return [];
  const first = node.firstChild;
  return first && first.isText ? first.marks : [];
};

/** Build an inline `text` fragment for a plain-text value. `\n` → a
 *  `hardBreak` node (or a space if the schema lacks one). When `marks` is
 *  provided, all emitted text nodes carry those marks. */
export const buildInlineNodes = (
  schema: Schema,
  value: string,
  marks: readonly Mark[] = []
): PMNode[] => {
  const hardBreakType = schema.nodes.hardBreak;
  const lines = value.split("\n");
  const out: PMNode[] = [];
  lines.forEach((line, idx) => {
    if (line) out.push(schema.text(line, marks.length ? marks : undefined));
    if (idx < lines.length - 1 && hardBreakType) {
      out.push(hardBreakType.create());
    } else if (idx < lines.length - 1) {
      out.push(schema.text(" "));
    }
  });
  return out;
};
